// Sync Service for Background Outbox Synchronization
import outbox from './outbox';
import voidOutbox from './voidOutbox';
import loginOutbox from './loginOutbox';
import connectivityMonitor from './connectivityMonitor';
import { Storage } from './Storage';
import axios from 'axios';
import store from '@/store';
import tableServerClient from '@/services/tableServerClient';
import { errorHandler } from '@/services/errorHandler';
// docs/FRONTEND.md § Sales-write user field — #26328 / Sentry 1V+1E
import { assertSalesWriteUser } from './salesWriteUser';
import {
    scheduleInvoiceSnapshot,
    scheduleTableSnapshot,
    pushInvoiceSnapshotNow
} from './deviceOfflineStatusService';

class SyncService {
    constructor() {
        this.isSyncing = false;
        this.syncInterval = null;
        this.retryDelays = [300000]; // 5 minutes
        this.currentRetryIndex = 0;
        this.listeners = [];
    }

    /**
     * Start the sync service
     * Begins periodic sync attempts when online
     */
    start() {

        // Listen to connectivity changes
        connectivityMonitor.subscribe('online', () => {
            // this.triggerSync(); // Removed to avoid immediate sync
            // Permanent-failed rows never enter getPending, so reconnect must still refresh
            // this PC's deviceOfflineStatus row for cashup on other tills.
            pushInvoiceSnapshotNow().catch(() => null);
        });

        // Recover orphaned 'sending' rows (process killed mid-upload) before the
        // first sync pass — otherwise they are silently skipped by getPending.
        Promise.all([
            outbox.resetStaleSending().catch(() => 0),
            voidOutbox.resetStaleVoidSending().catch(() => 0),
            loginOutbox.resetStaleSending().catch(() => 0)
        ]).catch(() => null).then(() => {
            if (connectivityMonitor.getStatus()) {
                this.triggerSync();
            }
            this._startPeriodicSync();
        });
    }

    /**
     * Stop the sync service
     */
    stop() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }

    /**
     * Start periodic sync with adaptive backoff
     */
    _startPeriodicSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
        }

        // Start with shorter interval, will adapt based on success/failure
        const interval = this.retryDelays[this.currentRetryIndex] || 300000;

        this.syncInterval = setInterval(() => {
            if (connectivityMonitor.getStatus() && !this.isSyncing) {
                this.triggerSync();
            }
        }, interval);
    }

    /**
     * Trigger immediate sync
     * @returns {Promise<Object>} - Sync results
     */
    async triggerSync() {
        if (this.isSyncing) {
            return { skipped: true };
        }

        if (!connectivityMonitor.getStatus()) {
            return { offline: true };
        }

        this.isSyncing = true;

        try {
            const result = await this._syncBatch();

            // Reset retry index on success
            if (result.synced > 0) {
                this.currentRetryIndex = 0;
                this._startPeriodicSync();
            }

            this._notifyListeners('syncComplete', result);
            return result;

        } catch (error) {
            console.error('[SyncService] Sync error:', error);

            // Increase retry delay on failure
            this.currentRetryIndex = Math.min(
                this.currentRetryIndex + 1,
                this.retryDelays.length - 1
            );
            this._startPeriodicSync();

            this._notifyListeners('syncError', { error });
            return { error: error.message };

        } finally {
            // Zero pending (or only permanent failed) never hits _syncItem finally;
            // still publish backlog so this PC's snapshot stays current while online.
            scheduleInvoiceSnapshot();
            this.isSyncing = false;
        }
    }

    /**
     * Sync a single item by its saleId
     * @param {String} saleId - The saleId of the invoice to sync
     * @returns {Promise<Object>} - Sync result
     */
    async syncItemById(saleId) {
        if (this.isSyncing) {
            return { skipped: true };
        }

        if (!connectivityMonitor.getStatus()) {
            return { offline: true };
        }

        const allItems = await outbox.getAllOutboxItems();
        const item = allItems.find(i => i.saleId === saleId);

        if (!item) {
            console.error(`[SyncService] Item with saleId ${saleId} not found for single sync.`);
            return { error: 'Item not found' };
        }

        this.isSyncing = true;
        let result;
        try {
            // Customer-first: ensure offline customers are created before syncing this invoice
            try {
                await store.dispatch('syncOfflineDebtors');
            } catch (e) {
                console.warn('[SyncService] Single-item sync: customer sync failed, continuing with invoice', e);
            }

            await this._syncItem(item);
            await this._reconcileStockAfterSalesSync([item.storeDB]);
            result = { saleId: item.saleId, status: 'synced' };
            this._notifyListeners('itemSynced', {
                saleId: item.saleId,
                clientSaleId: item.payload.clientSaleId
            });
        } catch (error) {
            result = { saleId: item.saleId, status: 'failed', error: error.message };
            this._notifyListeners('syncError', { error });
            console.error(`[SyncService] Failed to sync item ${saleId}:`, error);
        } finally {
            this.isSyncing = false;
        }
        return result;
    }

    /**
     * Sync a batch of pending sales, voids, and logins
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncBatch(batchSize = 10) {
        // Periodic sweep for rows orphaned in 'sending' by a previous crash or killed window.
        await Promise.all([
            outbox.resetStaleSending().catch(() => 0),
            voidOutbox.resetStaleVoidSending().catch(() => 0),
            loginOutbox.resetStaleSending().catch(() => 0)
        ]);

        // Sync sales
        const salesResults = await this._syncSalesBatch(batchSize);

        // Sync voids
        const voidsResults = await this._syncVoidBatch(batchSize);

        // Sync logins
        const loginsResults = await this._syncLoginBatch(batchSize);

        if (salesResults.synced > 0) {
            await this._reconcileStockAfterSalesSync(salesResults.syncedStoreDBs || []);
        }

        return {
            synced: salesResults.synced + voidsResults.synced + loginsResults.synced,
            failed: salesResults.failed + voidsResults.failed + loginsResults.failed,
            total: salesResults.total + voidsResults.total + loginsResults.total,
            sales: salesResults,
            voids: voidsResults,
            logins: loginsResults
        };
    }

    /**
     * Sync a batch of pending sales
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncSalesBatch(batchSize = 10) {
        // 1) Always sync customers first
        try {
            await store.dispatch('syncOfflineDebtors');
        } catch (e) {
            console.warn('[SyncService] Customer sync failed, will continue with invoices but they may still reference temp IDs', e);
        }

        const pending = await outbox.getPendingOrdered(batchSize);

        if (pending.length === 0) {
            return { synced: 0, failed: 0, total: 0, details: [] };
        }

        const results = {
            synced: 0,
            failed: 0,
            total: pending.length,
            details: [],
            syncedStoreDBs: []
        };
        const syncedStoreSet = new Set();

        for (const item of pending) {
            try {
                await this._syncItem(item);
                results.synced++;
                results.details.push({ saleId: item.saleId, status: 'synced' });
                if (item.storeDB) syncedStoreSet.add(item.storeDB);
            } catch (error) {
                results.failed++;
                results.details.push({
                    saleId: item.saleId,
                    status: 'failed',
                    error: error.message
                });
                console.error(`[SyncService] Failed to sync ${item.saleId}:`, error);
                // Strict ordering: do not continue with higher sale numbers.
                break;
            }
        }

        results.syncedStoreDBs = [...syncedStoreSet];
        return results;
    }

    /**
     * Sync a batch of pending voids
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncVoidBatch(batchSize = 10) {
        const pending = await voidOutbox.getPendingVoids(batchSize);

        if (pending.length === 0) {
            return { synced: 0, failed: 0, total: 0, details: [] };
        }

        const results = {
            synced: 0,
            failed: 0,
            total: pending.length,
            details: []
        };

        for (const item of pending) {
            try {
                await this._syncVoidItem(item);
                results.synced++;
                results.details.push({ voidId: item.voidId, status: 'synced' });
            } catch (error) {
                results.failed++;
                results.details.push({
                    voidId: item.voidId,
                    status: 'failed',
                    error: error.message
                });
                console.error(`[SyncService] Failed to sync void ${item.voidId}:`, error);
            }
        }

        return results;
    }

    /**
     * Sync a batch of pending login records
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncLoginBatch(batchSize = 50) {
        const pending = await loginOutbox.getPendingLogins(batchSize);

        if (pending.length === 0) {
            return { synced: 0, failed: 0, total: 0, details: [] };
        }

        const results = {
            synced: 0,
            failed: 0,
            total: pending.length,
            details: []
        };

        for (const item of pending) {
            try {
                await this._syncLoginItem(item);
                results.synced++;
                results.details.push({ loginId: item.id, status: 'synced' });
            } catch (error) {
                results.failed++;
                results.details.push({
                    loginId: item.id,
                    status: 'failed',
                    error: error.message
                });
                console.error(`[SyncService] Failed to sync login ${item.id}:`, error);
            }
        }

        return results;
    }

    /**
     * Sync a single item
     * @param {Object} item - Outbox item
     * @returns {Promise<void>}
     */
    async _syncItem(item) {
        // Mark as sending
        const itemStoreDb = item.storeDB || item.storeDb || '';
        await outbox.markSending(item.saleId, itemStoreDb);

        try {
            const storeDB = item.storeDB;
            if (!storeDB) throw new Error('Missing storeDB for sale sync');

            // CRITICAL: Use the original localId for proper idempotency
            // DO NOT regenerate on normal sync — same localId across retries so the
            // server can detect duplicates. Force Retry may mint a new id.
            console.log(`[SyncService] Syncing offline sale ${item.saleId} with original localId ${item.payload.localId}`);

            const payload = {
                ...item.payload,
                localId: item.payload.localId,
                offlineSaleNumber: item.saleId,
                saleDate: this.formatDateForSync(item.date)
            };
            // Fail-fast if outbox invoice lost cashier identity (#26328)
            assertSalesWriteUser(payload.user || payload.User, 'syncService._syncItem.invoiceMobile');

            // Remap customer temp IDs to real IDs if mapping exists
            try {
                const idMap = await Storage.getCustomerIdMap();
                if (payload && payload.customer && idMap && idMap[payload.customer]) {
                    payload.customer = idMap[payload.customer];
                }
                if (payload && payload.LPO && payload.LPO.debtorID && idMap && idMap[payload.LPO.debtorID]) {
                    payload.LPO.debtorID = idMap[payload.LPO.debtorID];
                }
            } catch (mapErr) {
                console.warn('[SyncService] Could not apply customer ID mapping', mapErr);
            }

            // Route through Main PC table server proxy for both main/non-main devices.
            const data = await tableServerClient.proxyInvoiceMobile(payload, storeDB);

            // Parse response if needed
            let parsedData = data;
            if (typeof data === 'string') {
                const correctedData = data.replaceAll(/NaN/g, '0');
                parsedData = JSON.parse(correctedData);
            }

            // Check if successful
            if (parsedData.status === 200 && parsedData.response) {
                // Mark as sent with server metadata
                await outbox.markSent(item.saleId, {
                    invoiceNo: parsedData.response.invoice,
                    serverInvoiceId: parsedData.response.invoiceId,
                    postedAt: new Date().toISOString()
                }, itemStoreDb);

                // Keep local sale seed monotonic after syncing offline invoices.
                try {
                    const payloadSale = Number(item?.payload?.saleNumber ?? item?.payload?.SaleNum ?? item?.payload?.orderNumber ?? 0);
                    const responseSale = Number(parsedData?.response?.saleNumber ?? parsedData?.response?.SaleNum ?? parsedData?.response?.saleNum ?? 0);
                    const maxKnown = Math.max(
                        Number.isFinite(payloadSale) && payloadSale > 0 ? Math.trunc(payloadSale) : 0,
                        Number.isFinite(responseSale) && responseSale > 0 ? Math.trunc(responseSale) : 0
                    );
                    if (maxKnown > 0) {
                        const currentNext = Number(localStorage.getItem('tableNextSaleNum') || 0);
                        const mergedNext = Math.max(
                            maxKnown + 1,
                            Number.isFinite(currentNext) && currentNext > 0 ? Math.trunc(currentNext) : 0
                        );
                        localStorage.setItem('tableNextSaleNum', String(mergedNext));
                    }
                } catch (_) { }

                // Notify listeners
                this._notifyListeners('itemSynced', {
                    saleId: item.saleId,
                    invoiceNo: parsedData.response.invoice,
                    clientSaleId: item.payload.clientSaleId
                });

                // Close held table locally — backend already accepted the invoice.
                // Mirrors the intent of finalizePaidTable for the offline→reconnect path
                // so the table-server SQLite row is removed and the next getAllTables
                // reconcile is consistent. Failure here is non-critical: the sync itself
                // has already succeeded, so we warn and continue.
                try {
                    const syncedSaleNumber = Number(
                        item?.payload?.saleNumber
                        ?? item?.payload?.SaleNum
                        ?? item?.payload?.orderNumber
                        ?? 0
                    );
                    if (syncedSaleNumber > 0 && storeDB) {
                        await tableServerClient.removeTable(syncedSaleNumber, storeDB, true);
                    }
                } catch (closeErr) {
                    console.warn('[SyncService] Failed to close held table after upload', closeErr);
                }

            } else {
                // Non-200 status code - store plain server message only
                const errorMessage =
                    errorHandler.toPlainErrorText(parsedData) ||
                    'Server returned non-200 status';
                await outbox.markFailed(item.saleId, errorMessage, true, itemStoreDb);

                console.error(`[SyncService] Server rejected ${item.saleId} with status ${parsedData.status}: ${errorMessage}`);

                throw new Error(`Server rejected invoice with status ${parsedData.status}: ${errorMessage}`);
            }

        } catch (error) {
            // Determine if error is retriable
            const isRetriable = this._isRetriableError(error);
            const failStoreDb = item.storeDB || item.storeDb || '';

            if (isRetriable) {
                // Check current attempts
                const currentAttempts = item.numberOfAttempts || 0;

                if (currentAttempts >= 5) {
                    const msg =
                        errorHandler.toPlainErrorText(error.response?.data) ||
                        error.message ||
                        'Max retries exceeded';
                    await outbox.markFailed(item.saleId, msg, false, failStoreDb);
                } else {
                    // Retry later
                    await outbox.incrementRetries(item.saleId, failStoreDb);
                }
            } else {
                // Non-retriable (validation, stock, already invoiced, etc.) — plain server text
                const msg =
                    errorHandler.toPlainErrorText(error.response?.data) ||
                    error.message ||
                    'Non-retriable error';
                await outbox.markFailed(item.saleId, msg, true, failStoreDb);
            }

            throw error;
        } finally {
            // Record offline backlog stats after every invoice sync attempt
            scheduleInvoiceSnapshot();
        }
    }

    /**
     * Sync a single void item
     * @param {Object} item - Void outbox item
     * @returns {Promise<void>}
     */
    async _syncVoidItem(item) {
        // Mark as sending
        await voidOutbox.markVoidSending(item.voidId);

        try {
            // Get store info
            const store = await Storage.getDefaultStore();

            // Post to server using the same endpoint as logItemVoid
            const url = serverApi + `supervisorOverrides/${store.StoreDB}/logItemVoid`;

            // Fail-fast if outbox void lost cashier identity (#26328)
            assertSalesWriteUser(item.payload?.User || item.payload?.user, 'syncService._syncVoidItem.logItemVoid');

            // Post void data
            await axios.post(url, item.payload, {
                timeout: 10000
            });

            // Mark as sent
            await voidOutbox.markVoidSent(item.voidId, {
                postedAt: new Date().toISOString()
            });

            // Notify listeners
            this._notifyListeners('voidSynced', {
                voidId: item.voidId
            });

        } catch (error) {
            // Determine if error is retriable
            const isRetriable = this._isRetriableError(error);

            if (isRetriable) {
                // Check current attempts
                const currentAttempts = item.numberOfAttempts || 0;

                if (currentAttempts >= 5) {
                    const msg =
                        errorHandler.toPlainErrorText(error.response?.data) ||
                        error.message ||
                        'Max retries exceeded';
                    await voidOutbox.markVoidFailed(item.voidId, msg);
                } else {
                    // Retry later
                    await voidOutbox.incrementVoidRetries(item.voidId);
                }
            } else {
                const msg =
                    errorHandler.toPlainErrorText(error.response?.data) ||
                    error.message ||
                    'Non-retriable error';
                await voidOutbox.markVoidFailed(item.voidId, msg);
            }

            throw error;
        } finally {
            scheduleTableSnapshot();
        }
    }

    /**
     * Sync a single login record
     * @param {Object} item - Login outbox item
     * @returns {Promise<void>}
     */
    async _syncLoginItem(item) {
        // Mark as sending
        await loginOutbox.markSending(item.id);

        try {
            // Ensure we have a storeDB
            let storeDB = item.storeDB;
            if (!storeDB) {
                try {
                    const store = await Storage.getDefaultStore();
                    if (store && store.StoreDB) {
                        storeDB = store.StoreDB;
                    }
                } catch (e) {
                    console.warn('[SyncService] Failed to recover storeDB for login sync', e);
                }
            }

            if (!storeDB) {
                throw new Error('Missing storeDB for login sync');
            }

            // Post to server using the same endpoint as recordlogin
            const url = serverApi + `supervisorOverrides/${storeDB}/recordLogin`;

            const payload = {
                // Fail-fast if outbox login lost username (#26328)
                username: assertSalesWriteUser(item.username, 'syncService._syncLoginItem.recordLogin'),
                platform: item.platform,
                pc: item.pc
            };

            // Post login data
            await axios.post(url, payload, {
                timeout: 10000
            });

            // Mark as sent
            await loginOutbox.markSent(item.id);

            // Notify listeners
            this._notifyListeners('loginSynced', {
                loginId: item.id,
                username: item.username
            });

        } catch (error) {
            // Determine if error is retriable
            const isRetriable = this._isRetriableError(error);

            if (isRetriable) {
                // Check current attempts
                const currentAttempts = item.attempts || 0;

                if (currentAttempts >= 5) {
                    // Too many retries, mark as failed
                    await loginOutbox.markFailed(item.id, true);
                } else {
                    // Retry later
                    await loginOutbox.incrementRetries(item.id);
                }
            } else {
                // Non-retriable error
                await loginOutbox.markFailed(item.id, true);
            }

            throw error;
        }
    }

    /**
     * Determine if an error is retriable
     * @param {Error} error - Error object
     * @returns {Boolean}
     */
    _isRetriableError(error) {
        // Server rejected with non-200 status - not retriable
        if (error.message && error.message.includes('Server rejected invoice')) {
            return false;
        }

        // Network errors are retriable
        if (!error.response) {
            return true;
        }

        // Server errors (5xx) are retriable
        if (error.response.status >= 500) {
            return true;
        }

        // Timeout errors are retriable
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            return true;
        }

        // Client errors (4xx) are generally not retriable
        return false;
    }

    /**
     * Get sync status
     * @returns {Promise<Object>} - Status info
     */
    async getStatus() {
        // Sales counts
        const salesPendingCount = await outbox.countByStatus('pending');
        const salesSendingCount = await outbox.countByStatus('sending');
        const salesSentCount = await outbox.countByStatus('sent');
        const salesFailedCount = await outbox.countByStatus('failed');

        // Void counts
        const voidsPendingCount = await voidOutbox.countVoidsByStatus('pending');
        const voidsSendingCount = await voidOutbox.countVoidsByStatus('sending');
        const voidsSentCount = await voidOutbox.countVoidsByStatus('sent');
        const voidsFailedCount = await voidOutbox.countVoidsByStatus('failed');

        return {
            pending: salesPendingCount + voidsPendingCount,
            sending: salesSendingCount + voidsSendingCount,
            sent: salesSentCount + voidsSentCount,
            failed: salesFailedCount + voidsFailedCount,
            sales: {
                pending: salesPendingCount,
                sending: salesSendingCount,
                sent: salesSentCount,
                failed: salesFailedCount
            },
            voids: {
                pending: voidsPendingCount,
                sending: voidsSendingCount,
                sent: voidsSentCount,
                failed: voidsFailedCount
            },
            isSyncing: this.isSyncing,
            isOnline: connectivityMonitor.getStatus()
        };
    }

    /**
     * Subscribe to sync events
     * @param {String} event - Event name ('syncComplete', 'syncError', 'itemSynced')
     * @param {Function} callback - Callback function
     * @returns {Function} - Unsubscribe function
     */
    subscribe(event, callback) {
        const listener = { event, callback };
        this.listeners.push(listener);

        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    /**
     * Notify listeners
     * @param {String} event - Event name
     * @param {Object} data - Event data
     */
    _notifyListeners(event, data) {
        this.listeners.forEach(listener => {
            if (listener.event === event || listener.event === 'all') {
                try {
                    listener.callback(data);
                } catch (error) {
                    console.error('Error in sync listener:', error);
                }
            }
        });
    }

    /**
     * Manual retry of failed items
     * @returns {Promise<Object>} - Retry results
     */
    async retryFailed() {
        // Get all failed items
        const allItems = await outbox.getAllOutboxItems();
        const failedItems = allItems.filter(item => item.status === 'failed');

        if (failedItems.length === 0) {
            return { message: 'No failed items to retry' };
        }

        // Reset them to pending
        for (const item of failedItems) {
            await outbox.incrementRetries(item.saleId, item.storeDB || item.storeDb);
        }

        // Trigger sync
        return this.triggerSync();
    }

    /**
     * Format date for sync payload
     * @param {String} dateString - Date string
     * @returns {String|null} - Formatted date string or null
     */
    formatDateForSync(dateString) {
        if (!dateString) return null;
        const date = new Date(dateString);

        const pad = (num) => num.toString().padStart(2, '0');

        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    async _reconcileStockAfterSalesSync(storeDbs = []) {
        const uniqueStoreDbs = [...new Set((storeDbs || []).filter(Boolean))];
        if (uniqueStoreDbs.length === 0) return;

        try {
            await store.dispatch('getAllStock');
        } catch (refreshErr) {
            console.warn('[SyncService] Stock refresh after sales sync failed', refreshErr);
        }

        for (const storeDb of uniqueStoreDbs) {
            try {
                const pendingStats = await outbox.getPendingStatsForStore(storeDb);
                if (Number(pendingStats?.count || 0) === 0) {
                    await Storage.clearStockDirtyPlus(storeDb);
                }
            } catch (dirtyErr) {
                console.warn(`[SyncService] Failed to reconcile dirty stock state for ${storeDb}`, dirtyErr);
            }
        }
    }
}

// Create singleton instance
const syncService = new SyncService();

export default syncService;
